import React, { useState, useMemo } from 'react';
import { Button } from '../ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { AlertTriangle, CheckCircle2, Package, X, AlertCircle, ArrowRight, Eye, RotateCcw, Truck } from 'lucide-react';

/**
 * LoaderPalletList - Handles pallet validation and displays current loading progress
 * 
 * Features:
 * - Validation phase: Show all pallets in proposed order with ability to mark issues
 * - Loading phase: Show current stack and progress
 * - Issue categories: missing, too_tall, not_toploadable, damaged, other
 * - Mobile-optimized with touch-friendly interactions
 */
export default function LoaderPalletList({ 
  planData, 
  onPalletMarkedProblem, 
  onValidationComplete, 
  onBackToValidation,
  currentPhase,
  isMobile 
}) {
  const [expandedPallet, setExpandedPallet] = useState(null);
  const [markingProblem, setMarkingProblem] = useState(null);
  const [selectedIssueReason, setSelectedIssueReason] = useState('missing');

  // Issue reason options
  const issueReasons = [
    { value: 'missing', label: 'Missing', icon: '❌', color: 'red', description: 'Pallet not found in warehouse or picking area' },
    { value: 'too_tall', label: 'Too Tall', icon: '📏', color: 'orange', description: 'Exceeds truck height limit - cannot fit' },
    { value: 'not_toploadable', label: 'Not Top-loadable', icon: '⚠️', color: 'yellow', description: 'Cannot have items stacked on top due to fragility' },
    { value: 'damaged', label: 'Damaged', icon: '💥', color: 'red', description: 'Physical damage prevents safe loading' },
    { value: 'weight_limit', label: 'Weight Limit', icon: '⚖️', color: 'orange', description: 'Exceeds weight capacity for vehicle or position' },
    { value: 'access_blocked', label: 'Access Blocked', icon: '🚫', color: 'red', description: 'Cannot access pallet due to obstruction' },
    { value: 'quality_hold', label: 'Quality Hold', icon: '🔍', color: 'purple', description: 'Quality control requires inspection before loading' },
    { value: 'wrong_product', label: 'Wrong Product', icon: '🔄', color: 'blue', description: 'Incorrect product or specification' },
    { value: 'customer_request', label: 'Customer Request', icon: '📞', color: 'blue', description: 'Customer requested to exclude this item' },
    { value: 'other', label: 'Other Issue', icon: '❓', color: 'gray', description: 'Other issue preventing loading' }
  ];

  // Get pallets organized by status
  const palletsByStatus = useMemo(() => {
    const placed = [];
    const current = [];
    const upcoming = [];
    const problems = [];

    planData.currentOrder.forEach((palletId, index) => {
      const pallet = planData.pallets.find(p => p.id === palletId);
      if (!pallet) return;

      const problemInfo = planData.problemPallets.find(p => p.id === palletId);
      const isPlaced = planData.placedPallets.includes(palletId);

      const palletWithStatus = {
        ...pallet,
        orderIndex: index,
        problemInfo,
        isPlaced,
        isCurrent: !isPlaced && !problemInfo && index === planData.placedPallets.length,
        estimatedWeight: pallet.weight || 0,
        dimensions: pallet.dimensions || {}
      };

      if (problemInfo) {
        problems.push(palletWithStatus);
      } else if (isPlaced) {
        placed.push(palletWithStatus);
      } else if (palletWithStatus.isCurrent) {
        current.push(palletWithStatus);
      } else {
        upcoming.push(palletWithStatus);
      }
    });

    return { placed, current, upcoming, problems };
  }, [planData]);

  // Get current stack info
  const currentStackInfo = useMemo(() => {
    if (!planData.stacks || planData.progress.currentStackIndex >= planData.stacks.length) {
      return null;
    }
    
    const stack = planData.stacks[planData.progress.currentStackIndex];
    const remainingInStack = stack.pallets.filter(p => 
      !planData.placedPallets.includes(p.id) && 
      !planData.problemPallets.some(pp => pp.id === p.id)
    ).length;

    return {
      ...stack,
      remainingInStack,
      totalInStack: stack.pallets.length
    };
  }, [planData]);

  const handleMarkProblem = (palletId, reason) => {
    onPalletMarkedProblem(palletId, reason);
    setMarkingProblem(null);
    setExpandedPallet(null);
  };

  const renderPalletCard = (pallet, showActions = false) => (
    <div
      key={pallet.id}
      className={`border rounded-lg transition-all duration-200 ${
        pallet.isPlaced ? 'bg-green-50 border-green-200' :
        pallet.problemInfo ? 'bg-red-50 border-red-200' :
        pallet.isCurrent ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-200' :
        'bg-white border-gray-200 hover:border-gray-300'
      }`}
    >
      <div 
        className="p-3 cursor-pointer"
        onClick={() => setExpandedPallet(expandedPallet === pallet.id ? null : pallet.id)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              pallet.isPlaced ? 'bg-green-100 text-green-800' :
              pallet.problemInfo ? 'bg-red-100 text-red-800' :
              pallet.isCurrent ? 'bg-blue-100 text-blue-800' :
              'bg-gray-100 text-gray-600'
            }`}>
              {pallet.isPlaced ? '✓' : 
               pallet.problemInfo ? '!' :
               pallet.isCurrent ? '▶' :
               pallet.orderIndex + 1}
            </div>
            
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <h4 className="font-medium text-sm">{pallet.name || `Pallet ${pallet.id}`}</h4>
                {pallet.isImportedPallet && (
                  <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
                    Imported
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500">
                {pallet.dimensions.width?.toFixed(1)}×{pallet.dimensions.depth?.toFixed(1)}×{pallet.dimensions.height?.toFixed(1)}m
                {pallet.estimatedWeight > 0 && ` • ${pallet.estimatedWeight}kg`}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {pallet.problemInfo && (
              <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">
                {issueReasons.find(r => r.value === pallet.problemInfo.reason)?.label || pallet.problemInfo.reason}
              </span>
            )}
            
            {pallet.isPlaced && <CheckCircle2 className="h-4 w-4 text-green-600" />}
            {pallet.isCurrent && <ArrowRight className="h-4 w-4 text-blue-600" />}
          </div>
        </div>

        {/* Expanded details */}
        {expandedPallet === pallet.id && (
          <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
            <div className="text-xs text-gray-600 space-y-1">
              <div>ID: {pallet.id}</div>
              <div>Attribute: {pallet.attribute || 'none'}</div>
              {pallet.notToploadable && <div className="text-orange-600">⚠️ Not top-loadable</div>}
              {pallet.position && (
                <div>Position: {pallet.position.map(p => p.toFixed(2)).join(', ')}</div>
              )}
            </div>

            {showActions && !pallet.isPlaced && !pallet.problemInfo && (
              <div className="flex space-x-2 pt-2">
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMarkingProblem(pallet.id);
                  }}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Mark Unplaceable
                </Button>
                
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => e.stopPropagation()}
                  className="px-3"
                  title="View Details"
                >
                  <Eye className="h-3 w-3" />
                </Button>
              </div>
            )}

            {showActions && pallet.problemInfo && (
              <div className="flex items-center justify-between pt-2">
                <span className="text-xs text-red-600 font-medium">
                  Issue: {issueReasons.find(r => r.value === pallet.problemInfo.reason)?.label || pallet.problemInfo.reason}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Remove from problem pallets (allow unmarking)
                    onPalletMarkedProblem(pallet.id, null);
                  }}
                  className="text-xs px-2 py-1 h-6"
                >
                  Clear Issue
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Validation Phase View
  if (currentPhase === 'validation') {
    return (
      <div className="h-full flex flex-col">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold mb-2">Validate Proposed Order</h2>
        <p className="text-sm text-gray-600 mb-3">
          Review the proposed loading order. Mark any pallets that have issues before starting.
        </p>

        {/* Validation Status */}
        {planData.problemPallets.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
            <div className="flex items-center space-x-2 text-amber-800">
              <AlertTriangle className="h-4 w-4" />
              <div className="flex-1">
                <div className="font-medium text-sm">
                  {planData.problemPallets.length} pallet{planData.problemPallets.length > 1 ? 's' : ''} marked as unplaceable
                </div>
                <div className="text-xs text-amber-700">
                  Plan will be recalculated without these pallets. They can be addressed separately.
                </div>
              </div>
            </div>
          </div>
        )}

        {planData.problemPallets.length === 0 && planData.currentOrder.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
            <div className="flex items-center space-x-2 text-green-800">
              <CheckCircle2 className="h-4 w-4" />
              <div className="text-sm">
                All {planData.currentOrder.length} pallets ready for loading
              </div>
            </div>
          </div>
        )}          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">
                {planData.currentOrder.length} pallets • {planData.problemPallets.length} issues
              </span>
              
              <Button
                onClick={onValidationComplete}
                disabled={planData.currentOrder.length === 0}
                size="sm"
              >
                Start Loading <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>

            {/* Quick Issue Buttons */}
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs font-medium text-gray-700 mb-2">Quick Mark Issues:</div>
              <div className="flex flex-wrap gap-1">
                {issueReasons.slice(0, 4).map((reason) => (
                  <Button
                    key={reason.value}
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedIssueReason(reason.value);
                      // Could add batch selection mode here if needed
                    }}
                    className="text-xs px-2 py-1 h-6"
                    title={`Quick select: ${reason.description}`}
                  >
                    <span className="mr-1">{reason.icon}</span>
                    {reason.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {planData.currentOrder.map((palletId, index) => {
            const pallet = planData.pallets.find(p => p.id === palletId);
            if (!pallet) return null;

            const problemInfo = planData.problemPallets.find(p => p.id === palletId);
            const palletWithStatus = {
              ...pallet,
              orderIndex: index,
              problemInfo,
              isPlaced: false,
              isCurrent: false
            };

            return renderPalletCard(palletWithStatus, true);
          })}
        </div>

        {/* Problem Marking Dialog */}
        {markingProblem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-4 border-b border-gray-200 bg-red-50">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                    <h3 className="text-lg font-semibold text-red-900">Mark Pallet Unplaceable</h3>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setMarkingProblem(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <div className="p-4 space-y-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <h4 className="font-medium text-gray-900">
                    {planData.pallets.find(p => p.id === markingProblem)?.name || `Pallet ${markingProblem}`}
                  </h4>
                  {(() => {
                    const pallet = planData.pallets.find(p => p.id === markingProblem);
                    return pallet ? (
                      <p className="text-sm text-gray-600">
                        {pallet.dimensions?.width?.toFixed(1)}×{pallet.dimensions?.depth?.toFixed(1)}×{pallet.dimensions?.height?.toFixed(1)}m
                        {pallet.weight && ` • ${pallet.weight}kg`}
                      </p>
                    ) : null;
                  })()}
                </div>
                
                <div>
                  <p className="text-sm text-gray-700 mb-3 font-medium">
                    Why can't this pallet be loaded?
                  </p>
                  
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {issueReasons.map((reason) => (
                      <label
                        key={reason.value}
                        className={`flex items-start space-x-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                          selectedIssueReason === reason.value 
                            ? `border-${reason.color}-500 bg-${reason.color}-50` 
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="issueReason"
                          value={reason.value}
                          checked={selectedIssueReason === reason.value}
                          onChange={(e) => setSelectedIssueReason(e.target.value)}
                          className={`mt-1 text-${reason.color}-600`}
                        />
                        <span className="text-xl flex-shrink-0">{reason.icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-gray-900">{reason.label}</div>
                          <div className="text-xs text-gray-600 mt-1">{reason.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5" />
                    <div className="text-xs text-yellow-800">
                      <p className="font-medium mb-1">This will trigger a replan</p>
                      <p>Marking this pallet as unplaceable will automatically recalculate the loading plan for remaining pallets.</p>
                    </div>
                  </div>
                </div>
                
                <div className="flex space-x-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setMarkingProblem(null)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleMarkProblem(markingProblem, selectedIssueReason)}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                    disabled={!selectedIssueReason}
                  >
                    <AlertTriangle className="h-4 w-4 mr-1" />
                    Mark Unplaceable
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Loading Phase View
  return (
    <div className="h-full flex flex-col">
      {/* Header with current stack info */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold">Loading Progress</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={onBackToValidation}
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            Back
          </Button>
        </div>
        
        {currentStackInfo && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-blue-900">
                  Stack {planData.progress.currentStackIndex + 1} of {planData.progress.totalStacks}
                </h3>
                <p className="text-sm text-blue-700">
                  {currentStackInfo.remainingInStack} pallets remaining in this stack
                </p>
              </div>
              <Truck className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        )}
      </div>

      {/* Status sections */}
      <div className="flex-1 overflow-y-auto">
        {/* Current Pallet(s) */}
        {palletsByStatus.current.length > 0 && (
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-medium text-blue-900 mb-2 flex items-center">
              <ArrowRight className="h-4 w-4 mr-1" />
              Next to Load
            </h3>
            <div className="space-y-2">
              {palletsByStatus.current.map(pallet => renderPalletCard(pallet, true))}
            </div>
          </div>
        )}

        {/* Upcoming Pallets */}
        {palletsByStatus.upcoming.length > 0 && (
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-medium text-gray-700 mb-2 flex items-center">
              <Package className="h-4 w-4 mr-1" />
              Upcoming ({palletsByStatus.upcoming.length})
            </h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {palletsByStatus.upcoming.slice(0, 10).map(pallet => renderPalletCard(pallet, true))}
            </div>
          </div>
        )}

        {/* Placed Pallets */}
        {palletsByStatus.placed.length > 0 && (
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-medium text-green-700 mb-2 flex items-center">
              <CheckCircle2 className="h-4 w-4 mr-1" />
              Completed ({palletsByStatus.placed.length})
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {palletsByStatus.placed.map(pallet => renderPalletCard(pallet))}
            </div>
          </div>
        )}

        {/* Problem Pallets */}
        {palletsByStatus.problems.length > 0 && (
          <div className="p-4 bg-red-50 border-t-2 border-red-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-red-800 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                Unplaceable Issues ({palletsByStatus.problems.length})
              </h3>
              
              {palletsByStatus.problems.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    // Clear all issues
                    palletsByStatus.problems.forEach(pallet => {
                      onPalletMarkedProblem(pallet.id, null);
                    });
                  }}
                  className="text-xs px-2 py-1 h-6"
                >
                  Clear All
                </Button>
              )}
            </div>

            {/* Issue Summary */}
            <div className="bg-white rounded-lg p-3 mb-3 border border-red-200">
              <div className="text-xs text-red-800 space-y-1">
                {issueReasons.map(reason => {
                  const count = palletsByStatus.problems.filter(p => p.problemInfo?.reason === reason.value).length;
                  if (count === 0) return null;
                  return (
                    <div key={reason.value} className="flex items-center justify-between">
                      <span className="flex items-center space-x-1">
                        <span>{reason.icon}</span>
                        <span>{reason.label}</span>
                      </span>
                      <span className="font-medium">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              {palletsByStatus.problems.map(pallet => renderPalletCard(pallet, true))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}